import type {
  CreateProjectInput,
  ProjectBreakdownView,
  SceneTraceRow,
  ProjectEstimate,
  ProjectRenderView,
  ProjectView,
  SceneCaptions,
  SceneType,
  SceneView,
  UpdateCaptionsInput,
} from '@alterium/shared';
import { api } from './client';

export const projectsApi = {
  list: () => api.get<ProjectView[]>('/projects'),
  get: (id: string) => api.get<ProjectView>(`/projects/${id}`),
  create: (input: Partial<CreateProjectInput> & { prompt: string }) =>
    api.post<ProjectView>('/projects', input),
  estimate: (id: string) => api.get<ProjectEstimate>(`/projects/${id}/estimate`),
  rebuild: (id: string) => api.post<ProjectView>(`/projects/${id}/rebuild`),
  generate: (id: string) =>
    api.post<{ queued: number; skipped: number; needsInput: number; project: ProjectView }>(
      `/projects/${id}/generate`,
    ),
  regenerateScene: (sceneId: string) =>
    api.post<{ success: boolean }>(`/projects/scenes/${sceneId}/regenerate`),
  remove: (id: string) => api.delete(`/projects/${id}`),

  insertScene: (projectId: string, afterSceneId: string | null, type: SceneType) =>
    api.post<ProjectView>(`/projects/${projectId}/scenes`, { afterSceneId, type }),
  updateScene: (sceneId: string, patch: Record<string, unknown>) =>
    api.patch<ProjectView>(`/projects/scenes/${sceneId}`, patch),
  duplicateScene: (sceneId: string) =>
    api.post<ProjectView>(`/projects/scenes/${sceneId}/duplicate`),
  moveScene: (sceneId: string, afterSceneId: string | null) =>
    api.post<ProjectView>(`/projects/scenes/${sceneId}/move`, { afterSceneId }),
  deleteScene: (sceneId: string) => api.delete<ProjectView>(`/projects/scenes/${sceneId}`),

  /** §21: вернуться к прошлому варианту сцены — каждый стоил единиц. */
  selectVariant: (sceneId: string, variantId: string) =>
    api.post<SceneView>(`/projects/scenes/${sceneId}/variants/${variantId}`),

  /** §20: из чего собран ролик. */
  breakdown: (id: string) => api.get<ProjectBreakdownView>(`/projects/${id}/breakdown`),

  /**
   * §51: чем сделана каждая сцена. Только для админа — §41 запрещает
   * показывать имена сервисов обычному пользователю.
   */
  trace: (id: string) => api.get<SceneTraceRow[]>(`/projects/${id}/trace`),

  /** §25: описание графики словами → структурная спецификация. */
  describeGraphics: (sceneId: string, text: string) =>
    api.post<SceneView>(`/projects/scenes/${sceneId}/graphics/describe`, { text }),

  /** §31: финальная сборка. */
  getRender: (id: string) => api.get<ProjectRenderView>(`/projects/${id}/render`),
  render: (id: string) => api.post<ProjectRenderView>(`/projects/${id}/render`),
  cancelRender: (id: string) => api.post<ProjectRenderView>(`/projects/${id}/render/cancel`),
  downloadUrl: (id: string) =>
    api.get<{ url: string; filename: string; expiresAt: string }>(`/projects/${id}/download`),

  /** §28: титры. */
  getCaptions: (sceneId: string) =>
    api.get<SceneCaptions & { generated: boolean }>(`/projects/scenes/${sceneId}/captions`),
  updateCaptions: (sceneId: string, input: UpdateCaptionsInput) =>
    api.patch<SceneCaptions>(`/projects/scenes/${sceneId}/captions`, input),
  regenerateCaptions: (sceneId: string) =>
    api.post<SceneCaptions>(`/projects/scenes/${sceneId}/captions/regenerate`),
};
